import gql from "graphql-tag";
import { getProductLink } from "~/core/utils/productNavigation";

// --- Local keyword-URL resolution (perf, 2026-07-20) ------------------------
// Every SPA navigation to a keyword URL (e.g. /sunglasses) paid a serial
// ~100-150ms `searchUrl` round trip before the page component could even
// start fetching its data. Two local sources eliminate that for most
// navigations:
//  1. a sessionStorage cache of previous positive resolutions (60 min TTL),
//  2. the header-menu data already in the store — its category/manufacturer
//     entries carry the exact keyword URLs the menu links navigate to.
// Unknown paths still resolve through the backend exactly as before.
const SEO_CACHE_KEY = "vf_seo_routes_v1";
const SEO_CACHE_TTL_MS = 60 * 60 * 1000;
const SEO_CACHE_MAX = 80;

function safeSeoError(error) {
  return String(error?.message || error || "Unknown error")
    .replace(/([?&](?:token|key|secret|password)=)[^&\s]+/gi, "$1[redacted]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 512);
}
const STATIC_SEO_ROUTES = Object.freeze({
  "/sunglasses": { type: "category", id: "59" },
  "/eyeglasses": { type: "category", id: "60" },
});

function normalizeSeoPath(value) {
  try {
    const parsed = new URL(String(value || "/"), "http://local.test");
    const path = parsed.pathname.replace(/\/+$/, "") || "/";
    return path.toLowerCase();
  } catch {
    const path = String(value || "/")
      .split(/[?#]/)[0]
      .replace(/\/+$/, "");
    return (path || "/").toLowerCase();
  }
}

function readSeoRouteCache() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SEO_CACHE_KEY);
    return raw ? JSON.parse(raw) || null : null;
  } catch {
    return null;
  }
}

function lookupCachedSeoRoute(path) {
  path = normalizeSeoPath(path);
  const cache = readSeoRouteCache();
  const entry = cache && cache[path];
  if (!entry || !entry.type || !entry.id) return null;
  if (!entry.created || Date.now() - entry.created > SEO_CACHE_TTL_MS) {
    return null;
  }
  return { type: entry.type, id: String(entry.id) };
}

function storeCachedSeoRoute(path, type, id) {
  path = normalizeSeoPath(path);
  if (typeof sessionStorage === "undefined" || !path || !type || !id) return;
  try {
    const cache = readSeoRouteCache() || {};
    const existing = cache[path];
    // Avoid rewriting the same mapping on every navigation (fast path hits
    // this on each query-only route change).
    if (
      existing &&
      existing.type === type &&
      String(existing.id) === String(id) &&
      existing.created &&
      Date.now() - existing.created < SEO_CACHE_TTL_MS / 2
    ) {
      return;
    }
    cache[path] = { type, id: String(id), created: Date.now() };
    const keys = Object.keys(cache);
    if (keys.length > SEO_CACHE_MAX) {
      keys.sort((a, b) => (cache[a]?.created || 0) - (cache[b]?.created || 0));
      while (keys.length > SEO_CACHE_MAX) {
        delete cache[keys.shift()];
      }
    }
    sessionStorage.setItem(SEO_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Quota exceeded / storage disabled — non-critical.
  }
}

function matchMenuUrl(list, path, type) {
  path = normalizeSeoPath(path);
  if (!Array.isArray(list)) return null;
  for (const item of list) {
    if (!item) continue;
    const url = normalizeSeoPath(item.url || "");
    if (url && url === path && item.id != null) {
      return { type, id: String(item.id) };
    }
    if (type === "category" && Array.isArray(item.categories)) {
      const child = matchMenuUrl(item.categories, path, type);
      if (child) return child;
    }
  }
  return null;
}

function resolveSeoRouteLocally(app, path) {
  const normalizedPath = normalizeSeoPath(path);
  const configured = STATIC_SEO_ROUTES[normalizedPath];
  if (configured) return configured;

  // Client-only: SSR must keep resolving through the backend (per-request
  // state, and the server has its own query micro-cache).
  if (typeof window === "undefined") return null;

  // Known cards and prefetched editorial links already carry the backend id.
  // SSR and unknown URLs retain authoritative backend resolution.
  const knownProduct = getProductLink(normalizedPath);
  if (knownProduct) return { type: "product", id: String(knownProduct.id) };

  // The exact clicked preview also covers model tiles.
  const preview = app.$store?.getters?.["store/product/get"];
  const member =
    preview?.modelVariants?.status === "ready"
      ? preview.modelVariants.variants?.find(
          (variant) => variant.path === normalizedPath
        )
      : null;
  if (
    member &&
    /^\d+$/.test(String(member.id || "")) &&
    Number(member.id) > 0
  ) {
    return { type: "product", id: String(member.id) };
  }
  if (
    preview?.__productNavigationPath === normalizedPath &&
    /^\d+$/.test(String(preview.id || "")) &&
    Number(preview.id) > 0
  ) {
    return { type: "product", id: String(preview.id) };
  }

  try {
    const menu =
      app.$store?.getters?.["apiHandler/getApiData"]?.("categoriesList");
    const category = matchMenuUrl(
      menu?.categoriesList?.content,
      normalizedPath,
      "category"
    );
    if (category) return category;
    const manufacturer = matchMenuUrl(
      menu?.manufacturerList?.content,
      normalizedPath,
      "manufacturer"
    );
    if (manufacturer) return manufacturer;
  } catch {
    // Malformed menu data — fall through to the backend query.
  }
  return lookupCachedSeoRoute(normalizedPath);
}

function normalizeResolvedType(type) {
  const value = String(type || "").trim();
  const aliases = {
    brand: "manufacturer",
    manufacturer_info: "manufacturer",
    "manufacturer-info": "manufacturer",
    manufacturerInfo: "manufacturer",
  };

  return aliases[value] || value;
}

function clearResolvedSeoRouteAfterNavigation(app, resolveSeq) {
  const clearIfStillCurrent = (route) => {
    if (resolveSeq !== app.__vfSeoResolveSeq) return;

    const currentRoute =
      route || app.$router.currentRoute?.value || app.$router.currentRoute;
    const matched = currentRoute?.matched || [];

    if (matched.length > 0 && matched[0].name === "_slug") return;

    app.$store.commit("vuefront/clearResolvedSeoRoute");
  };

  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    clearIfStillCurrent();
    return;
  }

  const clearAfterPaint = (route) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => clearIfStillCurrent(route));
    });
  };

  if (typeof app.$router.afterEach !== "function") {
    clearAfterPaint();
    return;
  }

  const removeHook = app.$router.afterEach((to, from, failure) => {
    removeHook();
    if (failure) return;

    clearAfterPaint(to);
  });
}

export async function seoResolver(app, to, from, next) {
  const toVal = to.value || to;
  const matched = app.$router.resolve(toVal.path).matched;

  if (matched.length > 0 && matched[0].name === "_slug") {
    // Perf (2026-07-02): if this exact path is already positively resolved,
    // skip the searchUrl round trip entirely. This covers two cases:
    //  1. Query-only navigation on a keyword URL (?filters/?page/?sort) — the
    //     path is unchanged, yet every filter/page click paid a serial
    //     ~100ms backend query before the route could proceed.
    //  2. First client-side run after SSR hydration — the server already
    //     resolved this path and the state arrived via window.__syncState__.
    // Path changes still resolve normally below.
    const resolved = app.$store?.state?.vuefront?.resolvedSeoRoute;
    if (
      resolved &&
      resolved.type &&
      resolved.id &&
      !resolved.notFound &&
      resolved.path === toVal.path
    ) {
      // Seed the local cache from SSR-hydrated / already-resolved state so a
      // later SPA return to this path skips the backend query too.
      storeCachedSeoRoute(toVal.path, resolved.type, resolved.id);
      return next();
    }

    // Resolve locally (sessionStorage cache or header-menu data) when
    // possible — skips the blocking searchUrl round trip entirely.
    const local = resolveSeoRouteLocally(app, toVal.path);
    if (local) {
      app.__vfSeoResolveSeq = (app.__vfSeoResolveSeq || 0) + 1;
      app.$store.commit("vuefront/setResolvedSeoRoute", {
        type: normalizeResolvedType(local.type),
        id: local.id,
        path: toVal.path,
      });
      storeCachedSeoRoute(
        toVal.path,
        normalizeResolvedType(local.type),
        local.id
      );
      return next();
    }

    app.__vfSeoResolveSeq = (app.__vfSeoResolveSeq || 0) + 1;
    const resolveSeq = app.__vfSeoResolveSeq;

    // Keep the previous page visible while the API resolves. Clearing here
    // causes a brief "Page Not Found" flash before the next page paints.

    try {
      const { data } = await app.$vfapollo.query({
        query: gql`
          query ($url: String) {
            searchUrl(url: $url) {
              url
              type
              id
            }
          }
        `,
        variables: {
          url: toVal.path,
        },
      });

      if (resolveSeq !== app.__vfSeoResolveSeq) {
        return next(false);
      }

      const { type, id } = data.searchUrl || {};
      const normalizedType = normalizeResolvedType(type);

      if (normalizedType && id) {
        app.$store.commit("vuefront/setResolvedSeoRoute", {
          type: normalizedType,
          id,
          path: toVal.path,
        });
        // Remember positive resolutions for future SPA navigations/reloads.
        storeCachedSeoRoute(toVal.path, normalizedType, id);
      } else {
        app.$store.commit("vuefront/setResolvedSeoRouteNotFound", {
          path: toVal.path,
        });
      }

      return next();
    } catch (e) {
      if (resolveSeq !== app.__vfSeoResolveSeq) {
        return next(false);
      }

      if (typeof window !== "undefined") {
        console.error("[seoResolver] Apollo error:", safeSeoError(e));
      }
      app.$store.commit("vuefront/clearResolvedSeoRoute");
      app.$store.commit("vuefront/setResponseError", e);
      if (typeof window === "undefined") throw e;
      return next();
    }
  }

  app.__vfSeoResolveSeq = (app.__vfSeoResolveSeq || 0) + 1;
  clearResolvedSeoRouteAfterNavigation(app, app.__vfSeoResolveSeq);
  return next();
}
